<template>
  <UPageCard
    class="mb-5"
    :ui="{
      root: 'overflow-hidden hover:shadow-xl',
      container: 'p-0 sm:p-0 md:p-0',
      body: 'w-full',
    }"
  >
    <template #body>
      <CottageListFavorite
        :item="{
          ...cottage,
          province: {
            slug: cottage.province.slug,
            name: cottage.province.name,
            url: '',
          },
          media: photoItems,
        }"
      />
      <div class="grid grid-cols-8 gap-2">
        <div class="col-span-8 md:col-span-3 relative">
          <div
            v-if="numberOfPhotoItems > 1"
            class="absolute bottom-4 right-4 z-10 md:hidden"
          >
            <span
              class="inline-flex items-center text-sm px-2.5 py-1.5 bg-black/60 text-white font-bold rounded-full"
            >
              {{
                t('views.cottage.gallery.legend', {
                  current: currentCarouselPage,
                  total: numberOfPhotoItems,
                })
              }}
            </span>
          </div>
          <UCarousel
            :key="carouselKey"
            ref="carouselRef"
            :items="photoItems"
            :ui="fullCarouselWithArrows"
            :arrows="true"
            @select="updateCurrentCarouselPage"
          >
            <template #default="media">
              <a
                :aria-label="
                  t('views.cottage-list.cottageCard.link', {
                    name: cottage.name,
                  })
                "
                :href="cottageDetailUrl"
                target="_blank"
              >
                <div
                  class="w-full h-full aspect-w-8 aspect-h-4 md:aspect-h-3 m-auto"
                >
                  <TLazyimg
                    :src="media.item.url"
                    classes-img="block w-full h-full object-cover"
                    :alt="media.item.alt"
                  />
                </div>
              </a>
            </template>
          </UCarousel>
        </div>

        <a
          class="col-span-8 md:col-span-5 p-3 md:p-4"
          :aria-label="
            t('views.cottage-list.cottageCard.link', { name: cottage.name })
          "
          :href="cottageDetailUrl"
          target="_blank"
        >
          <div>
            <TText
              tag="h2"
              size="lg"
              :classes="`mb-2 w-fit rounded ${cottage.isHighlight ? 'bg-primary-100 px-1' : ''}`"
            >
              {{ cottage.name }}
            </TText>

            <TText color="text-gray-600" size="sm" classes="mb-0">
              {{ cottage.village.name }}
            </TText>
            <div class="flex items-center mb-1.5 md:mb-3">
              <TStars :stars="Math.round(cottage.review.score)" />
              <TText class="ml-1" classes="mb-0" color="text-gray-600"
                >({{ cottage.review.numReviews }})</TText
              >
            </div>
            <div>
              <TText
                tag="div"
                color="text-gray-700"
                size="sm"
                clamp="clamp3"
                class="md:min-h-16"
              >
                <strong
                  v-if="
                    withDistance && cottage.distanceInKm && currentLocationName
                  "
                  class="mr-1"
                >
                  {{
                    t('views.cottage-list.cottageCard.distance', {
                      n: cottage.distanceInKm,
                      l: currentLocationName,
                    })
                  }}
                </strong>
                <div class="hidden md:block" v-html="cottage.description" />
              </TText>
            </div>

            <div
              class="grid gap-x-5 grid-cols-2 w-fit sm:flex sm:gap-x-8 mt-2 md:mt-4"
            >
              <div class="flex items-center my-1">
                <div
                  v-if="
                    cottage.rentType === 'FULL' || cottage.rentType === 'BOTH'
                  "
                  class="mr-1 flex items-center justify-center"
                >
                  <UIcon
                    :name="'i-heroicons-home'"
                    class="w-5 h-5"
                    aria-hidden="true"
                  />
                </div>
                <div
                  v-if="
                    cottage.rentType === 'ROOMS' || cottage.rentType === 'BOTH'
                  "
                  class="mr-1 flex items-center justify-center"
                >
                  <UIcon
                    :name="'i-fluent-conference-room-20-regular'"
                    class="w-5 h-5"
                    aria-hidden="true"
                  />
                </div>
                <div>
                  <TText size="xs" leading="none" classes="mb-0">
                    {{
                      (cottage.rentType === 'FULL' &&
                        t(
                          'views.cottage-list.cottageCard.visualInformation.rentTypeFull',
                        )) ||
                      (cottage.rentType === 'ROOMS' &&
                        t(
                          'views.cottage-list.cottageCard.visualInformation.rentTypeRooms',
                        )) ||
                      (cottage.rentType === 'BOTH' &&
                        t(
                          'views.cottage-list.cottageCard.visualInformation.rentTypeBoth',
                        ))
                    }}</TText
                  >
                </div>
              </div>
              <div class="flex items-center my-1">
                <div class="mr-1 flex items-center justify-center">
                  <UIcon
                    :name="'i-heroicons-user'"
                    class="w-5 h-5"
                    aria-hidden="true"
                  />
                </div>
                <div>
                  <TText size="xs" leading="none" classes="mb-0">
                    {{
                      cottage.capacity.min
                        ? `${cottage.capacity.min} - ${cottage.capacity.max} ${t(
                            'views.cottage-list.cottageCard.visualInformation.capacity',
                          )}`
                        : `${cottage.capacity.max} ${t(
                            'views.cottage-list.cottageCard.visualInformation.capacity',
                          )}`
                    }}
                  </TText>
                </div>
              </div>
              <div class="flex items-center my-1">
                <div class="mr-1 flex items-center justify-center">
                  <UIcon
                    :name="'i-fluent-door-tag-20-regular'"
                    class="w-5 h-5"
                    aria-hidden="true"
                  />
                </div>
                <div>
                  <TText size="xs" leading="none" classes="mb-0">
                    {{
                      cottage.numRooms > 1
                        ? t(
                            'views.cottage-list.cottageCard.visualInformation.numRooms',
                            cottage.numRooms,
                          )
                        : t(
                            'views.cottage-list.cottageCard.visualInformation.room',
                          )
                    }}</TText
                  >
                </div>
              </div>
              <div class="flex items-center my-1">
                <div class="mr-1 flex items-center justify-center">
                  <UIcon
                    :name="'i-fluent-bed-24-regular'"
                    class="w-5 h-5"
                    aria-hidden="true"
                  />
                </div>
                <div>
                  <TText size="xs" leading="none" classes="mb-0">
                    {{
                      cottage.numBeds > 1
                        ? t(
                            'views.cottage-list.cottageCard.visualInformation.numBeds',
                            cottage.numBeds,
                          )
                        : t(
                            'views.cottage-list.cottageCard.visualInformation.bed',
                          )
                    }}</TText
                  >
                </div>
              </div>
            </div>
          </div>

          <div class="flex flex-wrap gap-1 md:gap-2 my-3 md:my-4 md:min-h-6">
            <UBadge
              v-if="cottage.hasVerifiedCalendar"
              color="neutral"
              variant="subtle"
              size="md"
              icon="i-lucide-calendar-check"
              >{{
                t('views.cottage-list.cottageCard.tags.verifiedCalendar')
              }}</UBadge
            >
            <UBadge
              v-if="cottage.hasOnlineBooking"
              data-test="onlineBookingBadge"
              color="info"
              variant="subtle"
              size="md"
              :icon="
                cottage.confirmationType === 'IMMEDIATE'
                  ? 'i-lucide-zap'
                  : undefined
              "
              >{{
                t('views.cottage-list.cottageCard.tags.online-booking')
              }}</UBadge
            >
            <UBadge
              v-if="cottage.hasFreeCancellations"
              data-test="onlineBookingBadge"
              color="success"
              variant="subtle"
              size="md"
              >{{
                t('views.cottage-list.cottageCard.tags.freeCancellation')
              }}</UBadge
            >
            <div v-if="cottage.hasFreeCancellations" class="flex items-center">
              <UIcon
                name="i-heroicons-check-solid"
                class="w-5 text-green-600 mr-1"
              />
              <TText size="xs" classes="mb-0">{{
                t('views.cottage-prices.freeCancellationText')
              }}</TText>
            </div>
          </div>
          <template v-if="cottage.hasDeals && cottage.currentDeal !== null">
            <div
              class="flex md:flex-col items-center md:items-end gap-2 md:gap-1 w-full"
            >
              <UBadge
                color="error"
                variant="subtle"
                size="md"
                icon="i-lucide-tag"
              >
                {{ t('views.cottage-list.cottageCard.deal') }}
              </UBadge>

              <div class="flex items-center md:justify-end gap-1 w-full">
                <TText
                  size="lg"
                  color="text-gray-900"
                  classes="mb-0 line-through"
                >
                  {{ toEuro(cottage.currentDeal.oldPrice.amount) }}
                </TText>
                <TText
                  size="lg"
                  variant="semibold"
                  color="text-gray-900"
                  classes="mb-0"
                >
                  {{ toEuro(cottage.currentDeal.newPrice.amount) }}
                </TText>
              </div>
            </div>
          </template>

          <template v-else>
            <div class="flex flex-col items-start md:items-end gap-1 w-full">
              <template v-if="cottage.hasDeals && cottage.currentDeal === null">
                <UBadge
                  color="error"
                  variant="subtle"
                  size="md"
                  icon="i-lucide-tag"
                  class="mr-2"
                >
                  {{ t('views.cottage-list.cottageCard.dealAvailable') }}
                </UBadge>
              </template>

              <div class="flex items-center md:justify-end gap-1 w-full">
                <TText
                  size="lg"
                  variant="semibold"
                  color="text-gray-900"
                  classes="mb-0"
                >
                  {{ displayPrice }}
                </TText>
                <TText size="sm" color="text-gray-600" classes="mb-0">
                  {{
                    cottage.price
                      ? t('views.cottage-list.cottageCard.priceTotalText')
                      : t('views.cottage-list.cottageCard.priceAverageText')
                  }}
                </TText>
              </div>
            </div>
          </template>
          <!-- <UButton
              icon="i-heroicons-map-pin-20-solid"
              color="neutral"
              variant="outline"
              size="sm"
              @click.prevent="handleViewOnMap"
            >
              {{ t('views.cottage-list.cottageCard.viewOnMap') }}
            </UButton> -->
        </a>
      </div>
    </template>
  </UPageCard>
</template>

<script setup lang="ts">
import type { CottageType } from '~~/modules/api/rurality/v3/search/cottages/schema'
import { browserFetch as fetchCottageGallery } from '~~/modules/api/rurality/v3/search/cottage-gallery'
import type { MediaType } from '~~/modules/api/rurality/v3/search/cottage-gallery/schema'
import { fullCarouselWithArrows } from '~~/modules/design-system/utils/nuxt-ui-config/components/carousel'

const props = defineProps<{
  cottage: CottageType
  withDistance?: boolean
  currentLocationName?: string
}>()

// const emit = defineEmits<{
//   viewOnMap: [cottage: CottageType]
// }>()

const route = useRoute()

const cottageDetailUrl = computed(() => {
  let dateIn: string | undefined
  let dateOut: string | undefined

  if (route.query.d && typeof route.query.d === 'string') {
    const dates = route.query.d.split(',')
    if (dates.length === 2) {
      dateIn = dates[0]
      dateOut = dates[1]
    }
  }

  return routes().cottageDetail({
    slug: props.cottage.slug,
    province: props.cottage.province.slug,
    dateIn,
    dateOut,
    capacity: route.query.c ? String(route.query.c) : undefined,
  })
})

const carouselRef = ref()
const currentCarouselPage = ref(1)
const carouselKey = ref(0)

const initialPhotoItem = {
  type: 'PHOTO' as const,
  url: props.cottage.media.thumb,
  alt: props.cottage.name,
  width: 1600,
  height: 800,
}

const photoItems = ref([initialPhotoItem])

const getPhotoItems = (items: MediaType[]) => {
  return items
    .filter(item => item.type === 'PHOTO')
    .map(item => ({
      type: 'PHOTO' as const,
      url: item.url,
      alt: item.alt || props.cottage.name,
      width: item.width!,
      height: item.height!,
    }))
}

onMounted(async () => {
  try {
    const response = await fetchCottageGallery({ id: props.cottage.id })

    if (response.success && response.success.data) {
      const galleryPhotos = getPhotoItems(response.success.data.medias)

      if (galleryPhotos.length > 0) {
        photoItems.value = galleryPhotos
      }
    }
  } catch (error) {
    console.error('Error fetching cottage gallery:', error)
  }
})

const updateCurrentCarouselPage = (index: number) => {
  currentCarouselPage.value = index + 1
}

const numberOfPhotoItems = computed(() => {
  return photoItems.value.length
})

const displayPrice = computed(() => {
  if (props.cottage.price) {
    if (props.cottage.priceType === 'EXACT') {
      return toEuro(props.cottage.price.amount)
    }
    return t('views.cottage-list.cottageCard.priceTotal', {
      n: toEuro(props.cottage.price.amount),
    })
  } else {
    return toEuro(props.cottage.averagePrice?.amount)
  }
})

// function handleViewOnMap(event: Event) {
//   event.preventDefault()
//   event.stopPropagation()
//   emit('viewOnMap', props.cottage)
// }
</script>
